/** Filters passed to GET /lessons (subset used by dashboard hooks). */
import { serializeFilters } from './stableFilters';

export interface LessonListFilters {
  academyId?: string;
  termId?: string;
  coachId?: string;
  dayOfWeek?: number;
  available?: boolean;
  lessonType?: 'group' | 'private' | 'camp';
  activeStatus?: 'active' | 'inactive';
  academySportId?: string;
}

export interface BookingListFilters {
  academyId?: string;
  customerId?: string;
  lessonId?: string;
  status?: string;
}

export type ProgramsFetchKey = string;

export function programsFetchKey(
  academyId: string,
  includeInactive: boolean,
  academySportId?: string
): ProgramsFetchKey {
  const base = `${academyId}:${includeInactive ? 'all' : 'active'}`;
  return academySportId ? `${base}:${academySportId}` : base;
}

export const userAcademiesQueryKey = ['academies', 'user'] as const;

export const queryKeys = {
  lessons: {
    all: ['lessons'] as const,
    lists: () => [...queryKeys.lessons.all, 'list'] as const,
    list: (academyId: string, filters: LessonListFilters = {}) =>
      [...queryKeys.lessons.lists(), academyId, serializeFilters(filters)] as const,
    publicList: (academyId: string) =>
      [...queryKeys.lessons.lists(), 'public', academyId] as const,
    publicBrowse: (academyId: string, params: object = {}) =>
      [...queryKeys.lessons.lists(), 'public-browse', academyId, serializeFilters(params)] as const,
    details: () => [...queryKeys.lessons.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.lessons.details(), id] as const,
  },
  courts: {
    all: ['courts'] as const,
    lists: () => [...queryKeys.courts.all, 'list'] as const,
    list: (academyId: string) => [...queryKeys.courts.lists(), academyId] as const,
    details: () => [...queryKeys.courts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.courts.details(), id] as const,
  },
  coaches: {
    all: ['coaches'] as const,
    lists: () => [...queryKeys.coaches.all, 'list'] as const,
    list: (academyId: string) => [...queryKeys.coaches.lists(), academyId] as const,
    publicList: (academyId: string) => [...queryKeys.coaches.lists(), 'public', academyId] as const,
    details: () => [...queryKeys.coaches.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.coaches.details(), id] as const,
    inviteStatus: (academyId: string) =>
      [...queryKeys.coaches.list(academyId), 'inviteStatus'] as const,
    stats: (coachId: string, academyId: string) =>
      [...queryKeys.coaches.all, 'stats', coachId, academyId] as const,
  },
  locations: {
    all: ['locations'] as const,
    lists: () => [...queryKeys.locations.all, 'list'] as const,
    list: (academyId: string, includeInactive: boolean) =>
      [...queryKeys.locations.lists(), academyId, { includeInactive }] as const,
  },
  terms: {
    all: ['terms'] as const,
    lists: () => [...queryKeys.terms.all, 'list'] as const,
    list: (academyId: string) => [...queryKeys.terms.lists(), academyId] as const,
    publicList: (academyId: string) => [...queryKeys.terms.lists(), 'public', academyId] as const,
    details: () => [...queryKeys.terms.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.terms.details(), id] as const,
  },
  bookings: {
    all: ['bookings'] as const,
    lists: () => [...queryKeys.bookings.all, 'list'] as const,
    list: (filters: BookingListFilters = {}) =>
      [...queryKeys.bookings.lists(), filters] as const,
    paginated: (academyId: string, page: number, filters: object = {}) =>
      [...queryKeys.bookings.lists(), 'paginated', academyId, page, serializeFilters(filters)] as const,
    details: () => [...queryKeys.bookings.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.bookings.details(), id] as const,
  },
  programs: {
    all: ['programs'] as const,
    lists: () => [...queryKeys.programs.all, 'list'] as const,
    list: (academyId: string, includeInactive: boolean, academySportId?: string) =>
      [...queryKeys.programs.lists(), programsFetchKey(academyId, includeInactive, academySportId)] as const,
    publicList: (academyId: string) =>
      [...queryKeys.programs.lists(), 'public', academyId] as const,
  },
  academySports: {
    all: ['academySports'] as const,
    lists: () => [...queryKeys.academySports.all, 'list'] as const,
    list: (academyId: string) => [...queryKeys.academySports.lists(), academyId] as const,
    publicList: (academyId: string) =>
      [...queryKeys.academySports.lists(), 'public', academyId] as const,
  },
  ageGroups: {
    all: ['ageGroups'] as const,
    lists: () => [...queryKeys.ageGroups.all, 'list'] as const,
    list: (academyId: string, scope: 'public' | 'private' = 'private') =>
      [...queryKeys.ageGroups.lists(), academyId, scope] as const,
  },
  academies: {
    all: ['academies'] as const,
    /** Stable reference — do not allocate a new key array per call (breaks useCallback deps). */
    user: () => userAcademiesQueryKey,
    details: () => [...queryKeys.academies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.academies.details(), id] as const,
    publicBySlug: (slug: string) => [...queryKeys.academies.all, 'public', 'slug', slug] as const,
    publicBySubdomain: (label: string) =>
      [...queryKeys.academies.all, 'public', 'subdomain', label] as const,
    ownerStripeTier: (slug: string) =>
      [...queryKeys.academies.all, 'ownerStripeTier', slug] as const,
  },
  subscription: {
    all: ['subscription'] as const,
    current: () => [...queryKeys.subscription.all, 'current'] as const,
    plans: (region?: string | null, productLine?: 'academy' | 'club' | null) =>
      [...queryKeys.subscription.all, 'plans', region ?? 'default', productLine ?? 'all'] as const,
  },
  students: {
    all: ['students'] as const,
    lists: () => [...queryKeys.students.all, 'list'] as const,
    list: (academyId: string, page: number, filters: object = {}) =>
      [...queryKeys.students.lists(), academyId, page, serializeFilters(filters)] as const,
    stats: (academyId: string) => [...queryKeys.students.all, 'stats', academyId] as const,
  },
} as const;

/** Resource prefixes invalidated when switching academy context. */
export const ACADEMY_SCOPED_QUERY_ROOTS = [
  queryKeys.lessons.all,
  queryKeys.courts.all,
  queryKeys.coaches.all,
  queryKeys.terms.all,
  queryKeys.bookings.all,
  queryKeys.programs.all,
  queryKeys.ageGroups.all,
  queryKeys.locations.all,
  queryKeys.students.all,
  queryKeys.academySports.all,
] as const;
